import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { marcarCambiosSinGuardar } from '../../common/utils/cambiosSinGuardar';
import { MetasApiError, MetasColaboradorService } from '../services/metasColaborador.service';
import type { Aviso, Duplicado, FilaVista, SemanaTiendas, VistaTienda } from '../types/metas.types';
import { CODIGO_PENDIENTE_CATALOGO } from '../types/metas.types';
import { esBloqueoCatalogo } from '../utils/pendientes';
import {
  aCapturaFilas,
  aplicarCambio,
  CambioFila,
  cambiosDeCasa,
  hayCambios,
  mismosValores,
  motivoNoEnviar,
  tiendasDelSelector,
  valoresDe,
} from '../utils/captura';
import {
  esEditable,
  esSoloLecturaPorEstado,
  MENSAJE_AUTORIZADA_SOLO_ADMIN,
  MENSAJE_ENVIADA_SOLO_LECTURA,
} from '../utils/estado';
import { esFilaCasa, filaCasaNueva } from '../utils/casa';
import { metaTiendaComparable, totalesCaptura } from '../utils/totales';
import { erroresPorLlave, traducirMensajeValidacion } from '../utils/validacionCelda';
import type { TiendaParam } from '../utils/rutas';

type AvisoToast = (severidad: 'success' | 'warn' | 'error', resumen: string, detalle?: string) => void;

export interface ErrorPantalla {
  mensaje: string;
  errores: Aviso[];
  duplicados: Duplicado[];
  conflicto: boolean;
  /** Ofrecer «Recargar» aunque no sea un 409 (p. ej. gente nueva en la plantilla). */
  recargar?: boolean;
}

/**
 * Por qué se detuvo el guardado automático. `hastaEditar`: vuelve a intentarlo
 * con el siguiente cambio (un 422 que la persona corrige); si no, sólo tras
 * recargar o guardar a mano (un 409 volvería a fallar igual).
 */
export interface AutoguardadoDetenido {
  mensaje: string;
  hastaEditar: boolean;
}

/** El último guardado de ESTA tienda en esta sesión: si fue automático, el pie lo dice. */
export interface UltimoGuardado {
  auto: boolean;
}

export const llaveTienda = (chain: string, num: number) => `${chain}|${num}`;

/** Nombre con que la captura se anota en el registro de «cambios sin guardar». */
export const ORIGEN_CAPTURA = 'metas-colaborador.captura';

const conPunto = (t: string) => (/[.!?]$/.test(t.trim()) ? t.trim() : `${t.trim()}.`);

/**
 * El error de la API, listo para pintar. `enviadas` son las filas del cuerpo
 * del `PUT`, en orden: el ValidationPipe nombra la fila por su POSICIÓN
 * («filas.17.metaMonto …») y aquí se cambia por el nombre de la persona.
 */
export const aErrorPantalla = (e: unknown, enviadas: { nombre: string }[] = []): ErrorPantalla => {
  if (!(e instanceof MetasApiError))
    return { mensaje: (e as Error)?.message ?? String(e), errores: [], duplicados: [], conflicto: false };
  const m = e.data?.message;
  const mensaje =
    Array.isArray(m) && m.length
      ? m.map((x) => conPunto(traducirMensajeValidacion(String(x), enviadas))).join(' ')
      : e.message;
  return { mensaje, errores: e.errores, duplicados: e.duplicados, conflicto: e.esConflicto };
};

/** Por qué se detiene el guardado automático tras un fallo al guardar. */
export function detencionPorError(e: unknown): AutoguardadoDetenido {
  const status = e instanceof MetasApiError ? e.status : null;
  if (status === 409)
    return {
      mensaje: 'Otra persona modificó esta captura: recarga para ver lo último. El guardado automático se detuvo.',
      hastaEditar: false,
    };
  if (status === 503)
    return {
      mensaje: 'BUK o el ERP no respondieron: el guardado automático se detuvo. Guarda a mano en unos minutos.',
      hastaEditar: false,
    };
  if (status === 422 || status === 400)
    return {
      mensaje: 'Hay que corregir lo que se marca arriba: el guardado automático sigue cuando cambies algo.',
      hastaEditar: true,
    };
  return {
    mensaje: 'No se pudo guardar (¿se cortó la conexión?): el guardado automático sigue cuando cambies algo.',
    hastaEditar: true,
  };
}

/** Para el aviso cuando la respuesta llega con otra tienda o semana abierta. */
export const nombreDe = (v: VistaTienda) => `${v.tienda.chain} ${v.tienda.num} · ${v.tienda.nombre}`;

/** El aviso cuando `?tienda=` no está entre las tiendas de la cuenta esa semana. */
export const MENSAJE_TIENDA_DEL_ENLACE = 'La tienda del enlace no está entre las tuyas esta semana';

/**
 * Lo que se mandó en un guardado: para no perder lo que se escribió mientras
 * iba. Sin `agregadas` desde el 21-oct-2026: «Vendieron aquí» salió de la
 * pantalla (decisión del usuario). Desde la ronda F (23-oct-2026) `agregar` y
 * `quitar` sólo llevan la Casa, y salen de comparar `filas` con lo guardado.
 */
interface Enviado {
  filas: FilaVista[];
  nota: string;
}

interface Opciones {
  lunes: string;
  onAviso: AvisoToast;
  /** La pantalla lo usa para no cambiar de semana sin guardar o avisar. */
  onCambiosSinGuardar?: (sucio: boolean) => void;
  /** La tienda del enlace (`?tienda=VAZ-1`), que se consulta con la PRIMERA lista. */
  tiendaInicial?: TiendaParam | null;
  /**
   * Cada vez que se abre una tienda: la pantalla la escribe en el enlace. Con
   * `null`, la lista llegó y no quedó ninguna abierta: la pantalla la BORRA del
   * enlace (si no, un `&tienda=` que no se está viendo se compartía igual).
   */
  onTienda?: (t: TiendaParam | null) => void;
  /** La tienda se envió a autorización: la pantalla avisa a Autorización (lista y contador). */
  onEnviada?: () => void;
}

/**
 * La captura de UNA tienda: carga, edición local, guardar, enviar, versión y
 * errores (409/422/503). Sale de `CapturaTab` (fase 2b) para que la pantalla
 * sólo pinte, y para que el autoguardado use el MISMO guardar que el botón.
 *
 * La pantalla edita una COPIA local; guardar manda todas las filas con la
 * `version` que se vio, y la respuesta del servidor reemplaza la copia
 * (identidad, totales y avisos oficiales son siempre los suyos). Si otra
 * pestaña u otra persona guardó entre medias, la API responde 409 y se ofrece
 * recargar: pisar en silencio una captura ajena es peor que pedir que se repita.
 *
 * ⚠️ Toda respuesta se adopta SÓLO si sigue abierta la tienda y la semana que
 * la pidieron (contador `pedido`): una respuesta que llega tarde pintaba otra
 * tienda, y con ella su `version`, que el siguiente guardado mandaba a la URL
 * equivocada.
 *
 * ⚠️ Con autoguardado se sigue escribiendo MIENTRAS se guarda. Adoptar la
 * respuesta tal cual borraba esas teclas; `adoptarConservando` pone encima lo
 * que cambió desde que salió el guardado, y eso queda como «sin guardar».
 *
 * ⚠️ Un guardado detrás de otro, nunca dos a la vez: el segundo lleva la
 * versión que devolvió el primero (con dos a la vez, el segundo era un 409).
 */
export function useCapturaTienda({ lunes, onAviso, onCambiosSinGuardar, tiendaInicial, onTienda, onEnviada }: Opciones) {
  const onEnviadaRef = useRef(onEnviada);
  onEnviadaRef.current = onEnviada;
  const [lista, setLista] = useState<SemanaTiendas | null>(null);
  const [errorLista, setErrorLista] = useState<string | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const selRef = useRef(sel);
  selRef.current = sel;
  const tiendaPedida = useRef(tiendaInicial ?? null);
  const onTiendaRef = useRef(onTienda);
  onTiendaRef.current = onTienda;
  const onAvisoRef = useRef(onAviso);
  onAvisoRef.current = onAviso;
  useEffect(() => {
    if (!sel) return;
    const [chain, num] = sel.split('|');
    onTiendaRef.current?.({ chain, num: Number(num) });
  }, [sel]);

  const [vista, setVista] = useState<VistaTienda | null>(null);
  const [cargandoVista, setCargandoVista] = useState(false);
  const [error, setError] = useState<ErrorPantalla | null>(null);
  const [filas, setFilas] = useState<FilaVista[]>([]);
  const [nota, setNotaEstado] = useState('');
  const [trabajando, setTrabajando] = useState<null | 'guardar' | 'enviar' | 'devolver'>(null);
  // Desde el clic en «Enviar» hasta que termina (incluye el guardado previo):
  // la tabla no se edita. Lo tecleado mientras tanto no viajaba en ninguna
  // petición y la respuesta del envío lo borraba.
  const [enviando, setEnviando] = useState(false);
  const enviandoRef = useRef(false);
  const [detenido, setDetenido] = useState<AutoguardadoDetenido | null>(null);
  const [ultimoGuardado, setUltimoGuardado] = useState<UltimoGuardado | null>(null);
  // Cuántas vistas se han adoptado: quien usa el hook vuelve a pedir lo suyo
  // (la lista de suplentes) con cada una.
  const [adopciones, setAdopciones] = useState(0);

  // Lo ÚLTIMO de cada cosa, para lo que se lee después de un `await` (el valor
  // del render en que empezó ya es viejo). Se escriben en el render y, además,
  // al adoptar: el guardado que espera al anterior tiene que ver la versión
  // nueva aunque React no haya vuelto a pintar.
  const vistaRef = useRef<VistaTienda | null>(null);
  const filasRef = useRef<FilaVista[]>([]);
  const notaRef = useRef('');
  vistaRef.current = vista;
  filasRef.current = filas;
  notaRef.current = nota;

  // ------------------------------------------------------------ carga
  const adoptar = useCallback((v: VistaTienda) => {
    vistaRef.current = v;
    filasRef.current = v.colaboradores;
    notaRef.current = v.estado.notaSupervisor ?? '';
    setVista(v);
    setFilas(v.colaboradores);
    setNotaEstado(v.estado.notaSupervisor ?? '');
    setDetenido(null);
    setAdopciones((n) => n + 1);
  }, []);

  /**
   * Adopta lo que devolvió un guardado SIN perder lo que se escribió mientras
   * iba: las filas que cambiaron desde que salió conservan su valor local y la
   * nota nueva se queda. Todo eso sigue contando como «sin guardar».
   */
  const adoptarConservando = useCallback(
    (v: VistaTienda, enviado: Enviado) => {
      const actuales = filasRef.current;
      const notaActual = notaRef.current;
      const antes = new Map(enviado.filas.map((f) => [f.key, valoresDe(f)]));
      // La Casa (ronda F) que se quitó o se agregó MIENTRAS iba el guardado: la
      // respuesta todavía la trae (o no), y adoptarla tal cual la regresaba (o la borraba).
      const enPantalla = new Set(actuales.map((f) => f.key));
      const enviadas = new Set(enviado.filas.map((f) => f.key));
      const enRespuesta = new Set(v.colaboradores.map((f) => f.key));
      const quitadasDesde = new Set(enviado.filas.filter((f) => !enPantalla.has(f.key)).map((f) => f.key));
      const agregadasDesde = actuales.filter((f) => !enviadas.has(f.key) && !enRespuesta.has(f.key));
      const cambiadas = new Map(
        actuales
          .filter((f) => {
            const a = antes.get(f.key);
            return !!a && !mismosValores(a, valoresDe(f));
          })
          .map((f) => [f.key, f]),
      );
      if (!cambiadas.size && !quitadasDesde.size && !agregadasDesde.length && notaActual === enviado.nota) {
        adoptar(v);
        return;
      }
      const filasNuevas = v.colaboradores.filter((f) => !quitadasDesde.has(f.key)).map((f) => {
        const c = cambiadas.get(f.key);
        return c
          ? {
              ...f,
              metaMonto: c.metaMonto,
              sinMetaMotivo: c.sinMetaMotivo,
              sinMetaDetalle: c.sinMetaDetalle,
              apoyo: c.apoyo,
              // Por persona (21-oct-2026): lo marcado mientras iba el guardado también se queda.
              ...(c.transporte !== undefined ? { transporte: c.transporte } : {}),
              ...(c.bodegueroFijo !== undefined ? { bodegueroFijo: c.bodegueroFijo } : {}),
              ...(c.diasIncapacidad !== undefined ? { diasIncapacidad: c.diasIncapacidad } : {}),
            }
          : f;
      });
      filasNuevas.push(...agregadasDesde);
      vistaRef.current = v;
      filasRef.current = filasNuevas;
      setVista(v);
      setFilas(filasNuevas);
      setNotaEstado(notaActual);
      // Una respuesta 2xx quita cualquier detención (un 503 anterior), igual
      // que `adoptar`: si no, el pie seguía diciendo «se detuvo» y lo tecleado
      // después ya no se guardaba solo.
      setDetenido(null);
      setAdopciones((n) => n + 1);
    },
    [adoptar],
  );

  // Con su propio contador, como la vista: la lista de la semana anterior que
  // llegaba tarde pintaba estados de otra semana en el selector de tienda.
  const pedidoLista = useRef(0);
  const cargarLista = useCallback(async () => {
    const n = ++pedidoLista.current;
    setErrorLista(null);
    try {
      const l = await MetasColaboradorService.listarTiendas(lunes);
      if (n !== pedidoLista.current) return null;
      setLista(l);
      return l;
    } catch (e) {
      if (n !== pedidoLista.current) return null;
      setErrorLista((e as Error).message);
      return null;
    }
  }, [lunes]);

  // Las que se pueden capturar (el Excel sólo lleva éstas) y las del selector,
  // que decide la API (`verEnCaptura`): suma las enviadas y autorizadas de la
  // cuenta, de sólo lectura, y las del plazo para corregir un rechazo.
  const capturables = useMemo(() => (lista?.tiendas ?? []).filter((t) => t.puedeCapturar), [lista]);
  const enSelector = useMemo(() => (lista ? tiendasDelSelector(lista) : []), [lista]);

  /**
   * La tienda que queda abierta con una lista recién llegada: la elegida si
   * sigue en ella; si no, la del enlace (`?tienda=`, UNA vez); si hay una sola,
   * ésa. Si no queda ninguna, la pantalla la borra del enlace.
   *
   * Aparte del efecto de la semana para que «Reintentar» (tras fallar la
   * primera lista) también abra la del enlace o la única.
   */
  const elegirDeLista = useCallback((l: SemanaTiendas) => {
    const caps = tiendasDelSelector(l);
    const pedida = tiendaPedida.current;
    tiendaPedida.current = null;
    const actual = selRef.current;
    let nueva: string | null;
    if (actual && caps.some((t) => llaveTienda(t.chain, t.num) === actual)) nueva = actual;
    else if (pedida && caps.some((t) => t.chain === pedida.chain && t.num === pedida.num))
      nueva = llaveTienda(pedida.chain, pedida.num);
    else nueva = caps.length === 1 ? llaveTienda(caps[0].chain, caps[0].num) : null;
    // La del enlace no está (ajena, inexistente o fuera de esta semana): antes se
    // quitaba en silencio y quedaba «Elige una tienda» sin saber por qué.
    if (pedida && !caps.some((t) => t.chain === pedida.chain && t.num === pedida.num))
      onAvisoRef.current('warn', MENSAJE_TIENDA_DEL_ENLACE, `${pedida.chain} ${pedida.num}`);
    selRef.current = nueva;
    setSel(nueva);
    if (!nueva) onTiendaRef.current?.(null);
  }, []);

  /** «Reintentar» tras fallar la lista: la pide otra vez y abre la tienda que toca. */
  const reintentarLista = useCallback(async () => {
    const l = await cargarLista();
    if (l) elegirDeLista(l);
  }, [cargarLista, elegirDeLista]);

  // Semana nueva: lista nueva. Se conserva la tienda elegida si sigue en la
  // lista; si hay una sola, se abre sola.
  useEffect(() => {
    let vivo = true;
    setVista(null);
    setError(null);
    setLista(null);
    void cargarLista().then((l) => {
      if (!vivo || !l) return;
      elegirDeLista(l);
    });
    return () => {
      vivo = false;
    };
  }, [cargarLista, elegirDeLista]);

  // Sólo cuenta la respuesta del ÚLTIMO pedido.
  const pedido = useRef(0);
  // El pedido con que se adoptó la vista que hay en pantalla. Mientras carga
  // otra tienda (`pedidoVista !== pedido`), la de pantalla ya no es la abierta:
  // un guardado de ella tomaba el número del pedido NUEVO y su respuesta
  // pintaba la tienda vieja encima de la nueva.
  const pedidoVista = useRef(0);
  const cargarVista = useCallback(async () => {
    const n = ++pedido.current;
    if (!sel) {
      setVista(null);
      setCargandoVista(false);
      return;
    }
    const [chain, num] = sel.split('|');
    setCargandoVista(true);
    setError(null);
    try {
      const v = await MetasColaboradorService.getVistaTienda(lunes, chain, Number(num));
      if (n === pedido.current) {
        pedidoVista.current = n;
        adoptar(v);
        setUltimoGuardado(null);
      }
    } catch (e) {
      if (n !== pedido.current) return;
      setVista(null);
      setError(aErrorPantalla(e));
    } finally {
      if (n === pedido.current) setCargandoVista(false);
    }
  }, [sel, lunes, adoptar]);

  useEffect(() => {
    void cargarVista();
  }, [cargarVista]);

  // ------------------------------------------------------------ en vivo
  const sucio = vista ? hayCambios(vista.colaboradores, filas, vista.estado.notaSupervisor, nota) : false;
  /** Lo mismo, leído de lo último (para después de un `await`). */
  const estaSucio = useCallback(() => {
    const v = vistaRef.current;
    return !!v && hayCambios(v.colaboradores, filasRef.current, v.estado.notaSupervisor, notaRef.current);
  }, []);
  // Con BUK caído la API responde 503 al guardar o enviar: se ve, no se guarda.
  const plantillaIncompleta = !!vista && (vista.fuentePlantilla === 'erp' || vista.fichasBuk === false);
  // La primera vez no hay nada en la base: guardar materializa la plantilla.
  const necesitaGuardar = sucio || vista?.estado.estado === 'sin_captura';
  const cadena = vista?.tienda.chain;
  const numero = vista?.tienda.num;
  const errores = useMemo(
    () =>
      cadena !== undefined && numero !== undefined
        ? erroresPorLlave(filas, { chain: cadena, num: numero }, vista?.colaboradores ?? [])
        : new Map<string, Aviso[]>(),
    [filas, cadena, numero, vista?.colaboradores],
  );
  const erroresRef = useRef(errores);
  erroresRef.current = errores;
  const totales = useMemo(() => totalesCaptura(filas, metaTiendaComparable(vista?.metaTienda)), [filas, vista?.metaTienda]);
  // Enviada y autorizada son de SÓLO LECTURA (decisión del 29-sep). La API ya
  // lo dice con `permisos.capturar`; el estado se mira también por si llega una
  // respuesta de antes.
  const estadoSoloLectura = !!vista && esSoloLecturaPorEstado(vista.estado.estado);
  const soloLectura = !vista || !vista.permisos.capturar || !vista.semana.editable || estadoSoloLectura;
  const motivoNoCapturar =
    vista?.permisos.motivoNoCapturar ??
    (vista?.estado.estado === 'enviada'
      ? MENSAJE_ENVIADA_SOLO_LECTURA
      : vista?.estado.estado === 'autorizada'
        ? MENSAJE_AUTORIZADA_SOLO_ADMIN
        : null);

  /**
   * ¿Puede guardar solo? Estado editable, permiso, sin errores de celda, BUK
   * arriba y sin un fallo que lo haya detenido.
   */
  const puedeAutoguardar =
    !!vista && !soloLectura && esEditable(vista.estado.estado) && !plantillaIncompleta && errores.size === 0 && !detenido;
  const puedeAutoguardarRef = useRef(puedeAutoguardar);
  puedeAutoguardarRef.current = puedeAutoguardar;

  useEffect(() => {
    onCambiosSinGuardar?.(sucio);
    // El layout lo consulta antes de navegar por el menú o cerrar sesión.
    marcarCambiosSinGuardar(ORIGEN_CAPTURA, sucio);
  }, [sucio, onCambiosSinGuardar]);
  useEffect(() => () => marcarCambiosSinGuardar(ORIGEN_CAPTURA, false), []);

  // Cerrar la pestaña con cambios sin guardar pide confirmación al navegador
  // (lo que el autoguardado todavía no alcanzó a guardar).
  useEffect(() => {
    if (!sucio) return;
    const alSalir = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', alSalir);
    return () => window.removeEventListener('beforeunload', alSalir);
  }, [sucio]);

  const porEnviar = vista
    ? motivoNoEnviar({
        puedeCapturar: vista.permisos.capturar,
        motivoNoCapturar,
        semanaEditable: vista.semana.editable,
        estado: vista.estado.estado,
        cambiosSinGuardar: !!necesitaGuardar,
        erroresCelda: errores.size,
        pendientes: totales.pendientes,
        total: totales.total,
        bloqueoCatalogo: vista.permisos.bloqueoCatalogo ?? null,
      })
    : 'Elige una tienda.';

  // ------------------------------------------------------------ edición
  /** Un cambio del supervisor: reanuda el autoguardado que un 422 detuvo. */
  const alEditar = useCallback(() => {
    setDetenido((d) => (d && d.hastaEditar ? null : d));
  }, []);

  const cambiar = useCallback(
    (key: string, c: CambioFila) => {
      setFilas((fs) => fs.map((f) => (f.key === key ? aplicarCambio(f, c) : f)));
      alEditar();
    },
    [alEditar],
  );

  /** Reemplaza las filas de una vez («Copiar semana anterior»). */
  const reemplazarFilas = useCallback(
    (nuevas: FilaVista[]) => {
      setFilas(nuevas);
      alEditar();
    },
    [alEditar],
  );

  const setNota = useCallback(
    (v: string) => {
      setNotaEstado(v);
      alEditar();
    },
    [alEditar],
  );

  // Sin `agregar` ni `quitarAgregada` desde el 21-oct-2026: «Vendieron aquí»
  // salió de la pantalla (decisión del usuario). La API sigue mandando
  // `vendieronAqui` y lo sigue usando para casar lo ya guardado; aquí no se lee.

  // ------------------------------------------------------------ la Casa (ronda F, 23-oct-2026)
  // «+ Casa» y «Quitar la Casa» cambian la copia local, como cualquier tecla: el
  // autoguardado los manda (`agregar`/`quitar` del PUT). Sólo si la API anuncia
  // la Casa (`vista.casa`) y la cuenta puede capturarla (`puedeAgregar` o
  // `puedeQuitar`: el permiso de capturar, sin la cuenta de la tienda).
  const casa = vista?.casa ?? null;
  const permisoCasa = !!casa && (casa.puedeAgregar || casa.puedeQuitar);
  const casaEnPantalla = !!casa && filas.some((f) => f.key === casa.key);
  const puedeAgregarCasa = permisoCasa && !casaEnPantalla;
  const puedeQuitarCasa = permisoCasa && casaEnPantalla;

  const agregarCasa = useCallback(() => {
    const c = vistaRef.current?.casa;
    if (!c || !(c.puedeAgregar || c.puedeQuitar)) return;
    if (filasRef.current.some((f) => f.key === c.key)) return;
    // Si estaba guardada y se quitó sin guardar, vuelve la de la vista (con su meta).
    const guardada = vistaRef.current?.colaboradores.find((f) => f.key === c.key);
    const nueva = [...filasRef.current, guardada ?? filaCasaNueva(c)];
    filasRef.current = nueva;
    setFilas(nueva);
    alEditar();
  }, [alEditar]);

  const quitarCasa = useCallback(() => {
    const nueva = filasRef.current.filter((f) => !esFilaCasa(f));
    if (nueva.length === filasRef.current.length) return;
    filasRef.current = nueva;
    setFilas(nueva);
    alEditar();
  }, [alEditar]);

  /** Corrige el programa de suplente de UNA persona sin recargar (no cuenta como cambio). */
  const ponerEnFila = useCallback((key: string, cambio: Partial<FilaVista>) => {
    const poner = (fs: FilaVista[]) => fs.map((f) => (f.key === key ? { ...f, ...cambio } : f));
    setFilas(poner);
    setVista((x) => {
      if (!x) return x;
      const nueva = { ...x, colaboradores: poner(x.colaboradores) };
      vistaRef.current = nueva;
      return nueva;
    });
  }, []);

  // ------------------------------------------------------------ guardar y enviar
  const enVuelo = useRef<Promise<VistaTienda | null> | null>(null);

  /**
   * Guarda la captura. `silencioso`: sin el aviso de «Borrador guardado»;
   * `auto`: lo pidió el autoguardado (no avisa por errores de celda, que ya se
   * ven en su renglón).
   *
   * La tienda y la semana salen de la VISTA que se está editando, no de los
   * props: entre que se abrió la tienda y el clic pudo cambiar la semana de la
   * pantalla, y se guardaba lo de una semana en la URL de otra.
   */
  const guardar = useCallback(
    async (opc: { silencioso?: boolean; auto?: boolean } = {}): Promise<VistaTienda | null> => {
      // Uno detrás de otro: el siguiente lleva la versión que devolvió éste.
      while (enVuelo.current) await enVuelo.current.catch(() => null);
      const origen = vistaRef.current;
      if (!origen) return null;
      // Carga otra tienda (o se recarga ésta): lo de pantalla ya no es lo abierto.
      if (pedidoVista.current !== pedido.current) return null;
      // El autoguardado que esperaba mientras se envía: el envío ya lleva lo guardado.
      if (opc.auto && enviandoRef.current) return null;
      if (erroresRef.current.size) {
        if (!opc.auto)
          onAviso('error', 'Hay errores en la captura', 'Corrige los renglones marcados en rojo antes de guardar.');
        return null;
      }
      // El autoguardado que llega tarde (ya se guardó todo con el botón): nada que hacer.
      if (opc.auto && !estaSucio()) return origen;
      // El pedido de la VISTA que se guarda (no el último): si mientras va se
      // abre otra tienda, su respuesta ya no se pinta.
      const n = pedidoVista.current;
      const enviado: Enviado = { filas: filasRef.current, nota: notaRef.current };
      setTrabajando('guardar');
      if (!opc.auto) setError(null);
      const p = (async (): Promise<VistaTienda | null> => {
        try {
          // La Casa agregada o quitada (ronda F): sólo si la API la conoce; una
          // anterior respondería 400 a la llave `casa:` (y no ofrece «+ Casa»).
          const casaCambios = origen.casa ? cambiosDeCasa(origen.colaboradores, enviado.filas) : { agregar: [], quitar: [] };
          const v = await MetasColaboradorService.guardar(origen.semana.lunes, origen.tienda.chain, origen.tienda.num, {
            version: origen.estado.version,
            notaSupervisor: enviado.nota.trim() || null,
            filas: aCapturaFilas(enviado.filas),
            ...(casaCambios.agregar.length ? { agregar: casaCambios.agregar } : {}),
            ...(casaCambios.quitar.length ? { quitar: casaCambios.quitar } : {}),
          });
          // Si mientras tanto se abrió otra tienda u otra semana, esta respuesta
          // NO es de lo que hay en pantalla. Se guardó, eso sí: se dice.
          // «Con el supervisor» no es un borrador: lo que guarda son sus ajustes a
          // lo que mandó el gerente (revisión de la fase B, 21-oct-2026).
          const titulo = v.estado.estado === 'revision' ? 'Ajustes guardados' : 'Borrador guardado';
          if (n !== pedido.current) {
            if (!opc.silencioso) onAviso('success', titulo, `${nombreDe(origen)}: se guardó.`);
            return null;
          }
          adoptarConservando(v, enviado);
          setError(null);
          setUltimoGuardado({ auto: !!opc.auto });
          if (!opc.silencioso) onAviso('success', titulo, `${v.totales.pendientes} pendiente(s).`);
          void cargarLista();
          return v;
        } catch (e) {
          if (n !== pedido.current) return null;
          setError(aErrorPantalla(e, enviado.filas));
          setDetenido(detencionPorError(e));
          return null;
        } finally {
          setTrabajando(null);
        }
      })();
      enVuelo.current = p;
      try {
        return await p;
      } finally {
        if (enVuelo.current === p) enVuelo.current = null;
      }
    },
    [adoptarConservando, cargarLista, estaSucio, onAviso],
  );

  /** Espera a que termine el guardado que va en camino (antes de enviar). */
  const esperarGuardado = useCallback(async () => {
    while (enVuelo.current) await enVuelo.current.catch(() => null);
  }, []);

  const enviarAhora = async (antes?: () => Promise<boolean>) => {
    // «Enviar» guarda primero (lo que el autoguardado no alcanzó).
    if (antes) await antes();
    await esperarGuardado();
    let v: VistaTienda | null = vistaRef.current;
    if (estaSucio() || v?.estado.estado === 'sin_captura') {
      v = await guardar({ silencioso: true });
      if (!v) return;
    }
    if (!v) return;
    // La tabla no se edita mientras se envía, así que esto no debería pasar;
    // si pasa, lo que quedó sin guardar NO viaja en el envío: no se envía.
    if (estaSucio()) {
      onAviso('warn', 'No se envió', 'Hay cambios que no alcanzaron a guardarse: revísalos y vuelve a enviar.');
      return;
    }
    const n = pedidoVista.current;
    const origen = v;
    setTrabajando('enviar');
    setError(null);
    // El envío cuenta como petición en camino: ningún guardado sale en paralelo
    // (con la misma versión, uno de los dos era un 409).
    const post = MetasColaboradorService.enviar(origen.semana.lunes, origen.tienda.chain, origen.tienda.num, {
      version: origen.estado.version,
      notaSupervisor: notaRef.current.trim() || null,
    });
    const enCamino: Promise<VistaTienda | null> = post
      .then(
        (r) => r,
        () => null,
      )
      .finally(() => {
        if (enVuelo.current === enCamino) enVuelo.current = null;
      });
    enVuelo.current = enCamino;
    try {
      const r = await post;
      // Se vea o no esta tienda todavía: Autorización tiene que enterarse.
      onEnviadaRef.current?.();
      if (n !== pedido.current) {
        onAviso('success', 'Enviada al regional', `${nombreDe(origen)}: la revisa ${r.tienda.regional}.`);
        return;
      }
      adoptar(r);
      onAviso('success', 'Enviada al regional', `La revisa ${r.tienda.regional}.`);
      void cargarLista();
    } catch (e) {
      if (n !== pedido.current) return;
      await enCamino;
      const err = aErrorPantalla(e);
      // Un pendiente de catálogo que bloquea (fase 4): la API lo evaluó EN VIVO al
      // enviar y la vista que se tenía no lo decía. Se relee la tienda (su banner
      // y la marca de cada persona) y se dice por qué no se envió.
      if (e instanceof MetasApiError && e.status === 422 && esBloqueoCatalogo(err.errores)) {
        const n = err.errores.filter((a) => a.codigo === CODIGO_PENDIENTE_CATALOGO).length;
        if (!estaSucio()) await cargarVista();
        setError({
          ...err,
          mensaje: `No se envió: ${n === 1 ? 'un pendiente de catálogo lo impide' : `${n} pendientes de catálogo lo impiden`}. Se corrigen en su sistema de origen (o, si la persona no trabaja aquí, con «Tienda errónea»).`,
        });
        return;
      }
      // Llaves que la API marca y que NO están en la tabla: alguien entró a la
      // plantilla de BUK desde que se abrió la tienda.
      const conocidas = new Set(origen.colaboradores.map((f) => f.key));
      const nuevas = new Set(err.errores.map((a) => a.key).filter((k): k is string => !!k && !conocidas.has(k)));
      if (nuevas.size) {
        const quien = nuevas.size === 1 ? 'Llegó 1 persona nueva' : `Llegaron ${nuevas.size} personas nuevas`;
        if (!estaSucio()) {
          await cargarVista();
          setError({
            ...err,
            mensaje: `${quien} a la plantilla desde que abriste la tienda. Se recargó: dale meta o motivo y vuelve a enviarla.`,
          });
          return;
        }
        setError({
          ...err,
          mensaje: `${quien} a la plantilla desde que abriste la tienda. Recarga para verla y vuelve a enviarla.`,
          recargar: true,
        });
        return;
      }
      setError(err);
    } finally {
      setTrabajando(null);
    }
  };

  /** Envía la tienda a autorización. Mientras tanto (guardado previo incluido) la tabla no se edita. */
  const enviar = async (antes?: () => Promise<boolean>) => {
    if (!vistaRef.current || porEnviar || enviandoRef.current) return;
    if (pedidoVista.current !== pedido.current) return;
    enviandoRef.current = true;
    setEnviando(true);
    try {
      await enviarAhora(antes);
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
    }
  };

  /**
   * «Devolver al gerente» (21-oct-2026, fase B): una tienda «con el supervisor»
   * vuelve a borrador con un comentario para que el gerente la corrija desde la
   * app. Lo pendiente se guarda ANTES (los ajustes del supervisor no se pierden:
   * el gerente los ve) y la devolución lleva la versión que devolvió ese
   * guardado. La API sólo responde estado y versión: la tienda se relee.
   * `true` = se devolvió; si no, el error queda en pantalla (o, si es del
   * comentario, lo dice quien llama con `onError`).
   */
  const devolver = async (comentario: string, onError?: (e: unknown) => void): Promise<boolean> => {
    if (!vistaRef.current || enviandoRef.current || pedidoVista.current !== pedido.current) return false;
    enviandoRef.current = true;
    setEnviando(true);
    try {
      await esperarGuardado();
      let v: VistaTienda | null = vistaRef.current;
      // Lo que no se pudo guardar se dice EN el diálogo (si no, quedaba detrás de él).
      const sinGuardar = () =>
        onError?.(new Error('No se devolvió: hay cambios que no se pudieron guardar. Corrige lo marcado y vuelve a intentarlo.'));
      if (estaSucio()) {
        v = await guardar({ silencioso: true });
        if (!v) {
          sinGuardar();
          return false;
        }
      }
      if (!v || estaSucio()) {
        sinGuardar();
        return false;
      }
      const n = pedidoVista.current;
      const origen = v;
      setTrabajando('devolver');
      setError(null);
      const post = MetasColaboradorService.devolver(origen.semana.lunes, origen.tienda.chain, origen.tienda.num, {
        version: origen.estado.version,
        comentario,
      });
      const enCamino: Promise<VistaTienda | null> = post
        .then(
          () => null,
          () => null,
        )
        .finally(() => {
          if (enVuelo.current === enCamino) enVuelo.current = null;
        });
      enVuelo.current = enCamino;
      try {
        await post;
        // Se vea o no esta tienda todavía: Autorización y los contadores tienen que enterarse.
        onEnviadaRef.current?.();
        onAviso('success', 'Devuelta al gerente', `${nombreDe(origen)}: la ve en la app de la tienda con tu comentario.`);
        if (n === pedido.current) await cargarVista();
        void cargarLista();
        return true;
      } catch (e) {
        await enCamino;
        if (n !== pedido.current) return false;
        if (onError) onError(e);
        else setError(aErrorPantalla(e));
        return false;
      } finally {
        setTrabajando(null);
      }
    } finally {
      enviandoRef.current = false;
      setEnviando(false);
    }
  };

  const descartar = () => {
    if (vista) adoptar(vista);
  };

  return {
    // lista
    lista,
    errorLista,
    cargarLista,
    reintentarLista,
    capturables,
    enSelector,
    sel,
    setSel,
    // vista
    vista,
    vistaRef,
    cargandoVista,
    cargarVista,
    pedido,
    adopciones,
    error,
    setError,
    // edición
    filas,
    filasRef,
    nota,
    setNota,
    cambiar,
    reemplazarFilas,
    ponerEnFila,
    // la Casa (ronda F)
    agregarCasa,
    quitarCasa,
    puedeAgregarCasa,
    puedeQuitarCasa,
    // derivados
    sucio,
    estaSucio,
    plantillaIncompleta,
    necesitaGuardar,
    errores,
    totales,
    estadoSoloLectura,
    soloLectura,
    motivoNoCapturar,
    porEnviar,
    puedeAutoguardar,
    puedeAutoguardarRef,
    detenido,
    ultimoGuardado,
    // acciones
    trabajando,
    enviando,
    guardar,
    esperarGuardado,
    enviar,
    devolver,
    descartar,
  };
}

export type CapturaTienda = ReturnType<typeof useCapturaTienda>;

export default useCapturaTienda;
